import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import {
  ArrowRight,
  Building2,
  Eye,
  EyeOff,
  Loader2,
  Lock,
  Mail,
  User,
} from 'lucide-react'
import {
  PhoneInputField,
  validatePhone,
} from '../../components/ui/PhoneInputField'
import { AuthLayout } from '../../components/layout/AuthLayout'
import { GoogleSignInButton } from '../../components/GoogleSignInButton'
import { TurnstileWidget, useTurnstileConfig } from '../../components/TurnstileWidget'
import { useAuthStore } from '../../stores/authStore'
import { api, extractApiError } from '../../lib/api'
import { useDetectedCountry } from '../../lib/use-detected-country'
import { landingFor } from '../../lib/landing'
import {
  companyNameSchema,
  confirmPasswordRefine,
  emailSchema,
  nameSchema,
  passwordStrongSchema,
} from '../../lib/validators'

const schema = z
  .object({
    // All shared schemas live in `lib/validators.js` so a copy-edit
    // here propagates to every other form (Members, Profile,
    // Tenant Create, …). Sign-up applies the strict TLD whitelist
    // to cut down throwaway-domain spam, and uses the per-country
    // Contact Number validator unchanged.
    organisationName: companyNameSchema('Company Name'),
    fullName: nameSchema('Full Name'),
    emailId: emailSchema({ strictTld: true }),
    contactNumber: z
      .string()
      .optional()
      .or(z.literal(''))
      .superRefine((v, ctx) => {
        const r = validatePhone(v)
        if (!r.valid) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: r.message ?? 'Please enter a valid Contact Number.',
          })
        }
      }),
    password: passwordStrongSchema,
    confirmPassword: z.string({ message: 'Confirm Password is required.' }),
  })
  .superRefine(confirmPasswordRefine('password', 'confirmPassword'))

export function SignUpPage() {
  const navigate = useNavigate()
  // Referral program — `?ref=CODE` from a company's share link. Forwarded to
  // the backend, which attributes this signup to the referring company (a
  // PENDING referral). An unknown/stale code is ignored server-side and never
  // blocks registration.
  const [searchParams] = useSearchParams()
  const referralCode = (searchParams.get('ref') || '').trim() || null
  const { register: registerAccount, adoptGoogleSession, status, user } = useAuthStore()
  const [submitting, setSubmitting] = useState(false)
  // ONE step: the account form. Sign-up used to carry a second "pick a plan"
  // step (and a `?step=plan` URL mirror), which meant manual and Google
  // sign-up chose a plan in two different places — a picker here, and
  // /billing for Google. The picker moved to /billing so both paths share
  // one plan-selection surface, one coupon field and one activation path.
  // Registration therefore sends no `planTier`; the tenant is created with
  // `planSelectedAt: null` and the PlanGate routes the new Company Admin
  // straight to /billing.
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  // Server IP-detected country → phone dial code (follows the VPN, unlike the
  // browser-timezone guess). Null while loading / unresolvable → field default.
  const detectedCountry = useDetectedCountry()
  const turnstile = useTurnstileConfig()
  const [turnstileToken, setTurnstileToken] = useState('')

  // No plan catalogue is fetched here any more — /billing owns that, and
  // fetching it twice would be two sources for one decision.

  const form = useForm({
    // `onTouched`: validate a field once it has been blurred, then on every
    // change after that.
    //
    // Step 1 advances with `form.trigger()` rather than `handleSubmit`, so RHF
    // never sets `isSubmitted` — and its default `reValidateMode: 'onChange'`
    // is gated on that flag. The effect was that once step-1 validation had
    // run, CORRECTING a field left its error on screen until the user pressed
    // Continue again (QA-079). `onTouched` re-validates independently of
    // submission, so a fixed field clears immediately.
    mode: 'onTouched',
    resolver: zodResolver(schema),
    defaultValues: {
      organisationName: '',
      fullName: '',
      emailId: '',
      contactNumber: '',
      password: '',
      confirmPassword: '',
    },
  })

  useEffect(() => {
    if (status === 'authenticated' && user) {
      navigate(landingFor(user.role), { replace: true })
    }
  }, [status, user, navigate])

  async function onSubmit(values) {
    if (turnstile.enabled && !turnstileToken) {
      toast.error("Please complete the 'I'm not a robot' verification.")
      return
    }
    setSubmitting(true)
    try {
      await registerAccount({
        organisationName: values.organisationName.trim(),
        fullName: values.fullName.trim(),
        emailId: values.emailId.trim(),
        contactNumber: values.contactNumber?.trim() || undefined,
        password: values.password,
        // No `planTier` / `couponCode` — both now belong to /billing.
        referralCode: referralCode || undefined,
        turnstileToken: turnstileToken || undefined,
      })
      toast.success('Company registered successfully.')
      // No explicit navigate: `registerAccount` signs the user in, and the
      // authenticated effect above routes them. Because the tenant has no
      // `planSelectedAt`, the PlanGate then sends them to /billing — the same
      // canonical redirect a Google sign-up goes through, rather than a
      // second hard-coded one here.
    } catch (err) {
      const { message, fields } = extractApiError(err)
      // Every field the server can reject is rendered on THIS form now, so a
      // field error can simply be attached to its input — the old
      // bounce-back-to-step-1 dance existed only because the submit happened
      // from a plan step that showed none of these inputs.
      let mapped = false
      if (fields) {
        for (const [name, msg] of Object.entries(fields)) {
          form.setError(name, { message: msg })
          mapped = true
        }
        if (mapped) toast.error(message || 'Please review the highlighted fields.')
      }
      if (!mapped) toast.error(message)
    } finally {
      setSubmitting(false)
    }
  }

  const errors = form.formState.errors
  return (
    <AuthLayout
      centered
      title="Create Your Account"
      subtitle="Sign up to get started with TaskMagix"
    >
      {/* Google SSO — same component as Sign In. When SSO is
       *  enabled the user can skip the long form entirely; Google
       *  hands us name + email, and we collect Org Name + Contact
       *  via the Complete Profile modal on the dashboard. */}
      <SignUpGoogleSsoBlock
        onSuccess={adoptGoogleSession}
        referralCode={referralCode}
      />
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        noValidate
        className="space-y-4"
      >
        <IconInput
          id="organisationName"
          label="Company Name"
          required
          icon={Building2}
          autoComplete="organization"
          placeholder="Enter a Company Name"
          maxLength={100}
          error={errors.organisationName?.message}
          register={form.register('organisationName')}
        />
        <IconInput
          id="fullName"
          label="Full Name"
          required
          icon={User}
          autoComplete="name"
          placeholder="Enter the Full Name"
          maxLength={100}
          error={errors.fullName?.message}
          register={form.register('fullName')}
        />
        <IconInput
          id="emailId"
          label="Email ID"
          required
          type="email"
          icon={Mail}
          autoComplete="email"
          placeholder="Enter the Email ID"
          maxLength={254}
          error={errors.emailId?.message}
          register={form.register('emailId')}
        />
        <PhoneInputField
          // Remount once the IP country arrives so the new default dial code
          // applies; falls back to the field's own guess when null.
          key={detectedCountry ?? 'auto'}
          defaultCountry={detectedCountry ?? undefined}
          id="contactNumber"
          label="Contact Number"
          value={form.watch('contactNumber') ?? ''}
          onChange={(v) =>
            form.setValue('contactNumber', v, { shouldValidate: true })
          }
          error={errors.contactNumber?.message}
        />
        <IconPasswordInput
          id="password"
          label="Password"
          required
          autoComplete="new-password"
          placeholder="Enter the Password"
          visible={showPassword}
          onToggleVisible={() => setShowPassword((v) => !v)}
          error={errors.password?.message}
          register={form.register('password')}
        />
        <IconPasswordInput
          id="confirmPassword"
          label="Confirm Password"
          required
          autoComplete="new-password"
          placeholder="Re-enter Password"
          visible={showConfirmPassword}
          onToggleVisible={() => setShowConfirmPassword((v) => !v)}
          error={errors.confirmPassword?.message}
          register={form.register('confirmPassword')}
        />

        {/* Bot check. It used to sit on the plan step; that step is gone, so
         *  it moves here — this is the submitting form now, and the server
         *  verifies the token before it touches the database. */}
        {turnstile.enabled && turnstile.siteKey ? (
          <div className="pt-1">
            <TurnstileWidget
              siteKey={turnstile.siteKey}
              onToken={setTurnstileToken}
            />
          </div>
        ) : null}

        {/* Primary CTA — full width, gradient + arrow icon to
         *  match the Sign In button exactly. This now SUBMITS the
         *  registration rather than advancing to a plan step, so it carries
         *  the pending state the old plan-step button owned. */}
        <button
          type="submit"
          disabled={submitting || (turnstile.enabled && !turnstileToken)}
          className="group w-full h-12 rounded-xl text-white font-semibold inline-flex items-center justify-center gap-2 shadow-[0_8px_20px_-6px_rgba(2,54,123,0.35)] hover:shadow-[0_12px_28px_-6px_rgba(2,54,123,0.45)] transition-all cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
          style={{
            background:
              'linear-gradient(135deg, #02367B 0%, #006CA5 50%, #0496C7 100%)',
          }}
        >
          {submitting ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Creating your account…
            </>
          ) : (
            <>
              Continue
              <ArrowRight
                size={16}
                className="transition-transform group-hover:translate-x-0.5"
              />
            </>
          )}
        </button>

        <p className="text-center text-sm text-[color:var(--color-ink-muted)]">
          Already have an account?{' '}
          <Link
            to="/sign-in"
            className="font-semibold text-[color:var(--color-primary)] hover:text-[color:var(--color-primary-hover)]"
          >
            Sign In
          </Link>
        </p>
      </form>
    </AuthLayout>
  )
}


function IconInput({
  id,
  label,
  required,
  icon: Icon,
  type = 'text',
  autoComplete,
  placeholder,
  error,
  register,
}) {
  return (
    <div>
      <label
        htmlFor={id}
        className="block w-fit mb-1.5 text-[14px] font-medium text-[color:var(--color-ink-strong)]"
      >
        {label}
        {required ? (
          <span
            aria-hidden="true"
            className="ml-0.5 text-[color:var(--color-priority-urgent)]"
          >
            *
          </span>
        ) : null}
      </label>
      <div className="relative">
        <Icon
          size={16}
          aria-hidden="true"
          className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--color-ink-muted)]"
        />
        <input
          id={id}
          type={type}
          autoComplete={autoComplete}
          placeholder={placeholder}
          // Same wiring the shared TextField already does. This page hand-rolls
          // its inputs, so the association has to be repeated here or a screen
          // reader is never told which field failed or why (defect D-A11Y-01).
          aria-invalid={Boolean(error)}
          aria-required={required || undefined}
          aria-describedby={error ? `${id}-error` : undefined}
          className={
            'w-full h-11 pl-10 pr-3 rounded-xl border bg-[color:var(--color-card)] text-[color:var(--color-ink)] outline-none transition-colors focus:border-[color:var(--color-primary)] ' +
            (error
              ? 'border-[color:var(--color-priority-urgent)]'
              : 'border-[color:var(--color-rule)]')
          }
          {...register}
        />
      </div>
      {error ? (
        <p
          id={`${id}-error`}
          role="alert"
          className="mt-1 text-xs text-[color:var(--color-danger-text)]"
        >
          {error}
        </p>
      ) : null}
    </div>
  )
}

/** Password counterpart to `IconInput` — `Lock` icon prefix + eye
 *  toggle suffix. Caller owns the visibility state so the page can
 *  manage Password vs Confirm-Password independently. */
function IconPasswordInput({
  id,
  label,
  required,
  autoComplete,
  placeholder,
  visible,
  onToggleVisible,
  error,
  register,
}) {
  return (
    <div>
      <label
        htmlFor={id}
        className="block w-fit mb-1.5 text-[14px] font-medium text-[color:var(--color-ink-strong)]"
      >
        {label}
        {required ? (
          <span
            aria-hidden="true"
            className="ml-0.5 text-[color:var(--color-priority-urgent)]"
          >
            *
          </span>
        ) : null}
      </label>
      <div className="relative">
        <Lock
          size={16}
          aria-hidden="true"
          className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--color-ink-muted)]"
        />
        <input
          id={id}
          type={visible ? 'text' : 'password'}
          autoComplete={autoComplete}
          placeholder={placeholder}
          maxLength={128}
          aria-invalid={Boolean(error)}
          aria-required={required || undefined}
          aria-describedby={error ? `${id}-error` : undefined}
          className={
            'w-full h-11 pl-10 pr-10 rounded-xl border bg-[color:var(--color-card)] text-[color:var(--color-ink)] outline-none transition-colors focus:border-[color:var(--color-primary)] ' +
            (error
              ? 'border-[color:var(--color-priority-urgent)]'
              : 'border-[color:var(--color-rule)]')
          }
          {...register}
        />
        <button
          type="button"
          onClick={onToggleVisible}
          aria-label={visible ? 'Hide password' : 'Show password'}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
        >
          {visible ? <Eye size={16} /> : <EyeOff size={16} />}
        </button>
      </div>
      {error ? (
        <p
          id={`${id}-error`}
          role="alert"
          className="mt-1 text-xs text-[color:var(--color-danger-text)]"
        >
          {error}
        </p>
      ) : null}
    </div>
  )
}

/** Sign-up variant of the Google block — same UX as the Sign In page,
 *  different copy on the divider. Self-hides when SSO is disabled. */
function SignUpGoogleSsoBlock({ onSuccess, referralCode }) {
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    let cancelled = false
    api
      .get('/auth/google/config')
      .then((res) => {
        if (!cancelled) setVisible(Boolean(res.data?.enabled && res.data?.clientId))
      })
      .catch(() => {
        if (!cancelled) setVisible(false)
      })
    return () => {
      cancelled = true
    }
  }, [])
  if (!visible) return null
  return (
    <div className="mb-4">
      <GoogleSignInButton mode="signup" onSuccess={onSuccess} referralCode={referralCode} />
      <div className="flex items-center gap-3 mt-4 text-[11px] uppercase tracking-[0.12em] font-semibold text-[color:var(--color-ink-subtle)]">
        <div className="flex-1 h-px bg-[color:var(--color-rule)]" />
        Or sign up with email
        <div className="flex-1 h-px bg-[color:var(--color-rule)]" />
      </div>
    </div>
  )
}
